// Step 5 of the client-side X12 pipeline for the 856 (Ship Notice/Manifest):
// walks one transaction's flat body segments (from step 2's envelope tree)
// into a header, a generic HL (Hierarchical Level) tree, and a summary. The
// 856 has no fixed loop depth like 850/852's header→name→item→summary
// phases — Shipment/Order/Pack/Tare/Item levels nest however HL02 (parent
// id) says they do, per whatever structure BSN05 declares — so this builds
// an id→node map from every HL occurrence and attaches each to its parent
// afterward, rather than tracking a fixed phase sequence. Only ever runs
// against a transaction whose ST01 is "856"; anything else is left alone so
// it can still be shown, read-only, by the UI.
//
// Never throws: an HL02 that doesn't resolve to an earlier HL01 leaves that
// node unattached (it surfaces as a root alongside real top-level Shipments)
// rather than aborting the parse — the validator's job is to flag that, not
// this module's.

import type {EdiElementValue, EdiSegment} from './tokenizer';

export interface Header856 {
  bsn?: EdiSegment;
}

export interface NameLoop856 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
}

export interface HLNode856 {
  hl: EdiSegment;
  /** HL01 — this level's own id. */
  id: string;
  /** HL02 — the id of the level this one nests under; absent for a
   * top-level Shipment. */
  parentId?: string;
  /** HL03 — which kind of level this is ('S', 'O', 'P', 'T' or 'I'). */
  levelCode?: string;
  td1: EdiSegment[];
  td5: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  fob?: EdiSegment;
  dtm: EdiSegment[];
  nameLoops: NameLoop856[];
  prf?: EdiSegment;
  man: EdiSegment[];
  lin?: EdiSegment;
  sn1?: EdiSegment;
  pid: EdiSegment[];
  /** Any segment inside this HL loop that attachHLSegment doesn't
   * recognize — retained (rather than dropped) so it can still be shown,
   * read-only, as an "unrecognized segment". */
  other: EdiSegment[];
  children: HLNode856[];
}

export interface Summary856 {
  ctt?: EdiSegment;
}

export interface Parsed856 {
  header: Header856;
  /** Root HL nodes: real top-level Shipments (no HL02) and any node whose
   * HL02 didn't resolve to an earlier HL01 — surfaced rather than dropped. */
  shipments: HLNode856[];
  summary: Summary856;
}

function elementAsString(
  value: EdiElementValue | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value[0] : value;
}

function emptyHLNode(hl: EdiSegment): HLNode856 {
  return {
    hl,
    id: elementAsString(hl.elements[0]) ?? '',
    parentId: elementAsString(hl.elements[1]) || undefined,
    levelCode: elementAsString(hl.elements[2]),
    td1: [],
    td5: [],
    refs: [],
    dtm: [],
    nameLoops: [],
    man: [],
    pid: [],
    other: [],
    children: [],
  };
}

function attachNameLoopSegment(loop: NameLoop856, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    default:
      break;
  }
}

/**
 * Deliberately level-agnostic: accepts any of its known segment types
 * regardless of the current node's HL03 level code (e.g. a Pack carrying
 * LIN/SN1 would be accepted) — the plan didn't scope attachment per level,
 * so this doesn't either.
 */
function attachHLSegment(
  node: HLNode856,
  s: EdiSegment
): NameLoop856 | undefined {
  switch (s.id) {
    case 'TD1':
      node.td1.push(s);
      return undefined;
    case 'TD5':
      node.td5.push(s);
      return undefined;
    case 'REF':
      node.refs.push(s);
      return undefined;
    case 'PER':
      node.per = s;
      return undefined;
    case 'FOB':
      node.fob = s;
      return undefined;
    case 'DTM':
      node.dtm.push(s);
      return undefined;
    case 'PRF':
      node.prf = s;
      return undefined;
    case 'MAN':
      node.man.push(s);
      return undefined;
    case 'LIN':
      node.lin = s;
      return undefined;
    case 'SN1':
      node.sn1 = s;
      return undefined;
    case 'PID':
      node.pid.push(s);
      return undefined;
    case 'N1': {
      const loop: NameLoop856 = {n1: s};
      node.nameLoops.push(loop);
      return loop;
    }
    default:
      node.other.push(s);
      return undefined;
  }
}

type Phase = 'header' | 'hl' | 'summary';

/**
 * Builds the header/HL-tree/summary structure for one 856 transaction's
 * segments. Never throws — a malformed document (an unresolvable HL02, a
 * stray or unexpected segment) is left partially structured rather than
 * aborting the parse; flagging that is the validator's job, not this one's.
 */
export function parse856Segments(segments: EdiSegment[]): Parsed856 {
  const header: Header856 = {};
  const summary: Summary856 = {};
  const nodesById = new Map<string, HLNode856>();
  const orderedNodes: HLNode856[] = [];

  let phase: Phase = 'header';
  let currentNode: HLNode856 | undefined;
  let currentNameLoop: NameLoop856 | undefined;

  for (const s of segments) {
    if (s.id === 'HL') {
      phase = 'hl';
      currentNode = emptyHLNode(s);
      currentNameLoop = undefined;
      if (currentNode.id && !nodesById.has(currentNode.id)) {
        nodesById.set(currentNode.id, currentNode);
      }
      orderedNodes.push(currentNode);
      continue;
    }

    if (s.id === 'CTT') {
      phase = 'summary';
      summary.ctt = s;
      currentNode = undefined;
      currentNameLoop = undefined;
      continue;
    }

    if (phase === 'header') {
      if (s.id === 'BSN') header.bsn = s;
      continue;
    }

    if (phase === 'hl' && currentNode) {
      if (
        s.id === 'N2' ||
        s.id === 'N3' ||
        s.id === 'N4' ||
        s.id === 'N5' ||
        s.id === 'N6'
      ) {
        if (currentNameLoop) attachNameLoopSegment(currentNameLoop, s);
        continue;
      }
      const startedNameLoop = attachHLSegment(currentNode, s);
      if (startedNameLoop) currentNameLoop = startedNameLoop;
    }
  }

  // Resolves each node's parent only against HL01 ids seen earlier in
  // document order (seenIds), not against the fully-populated nodesById
  // map — otherwise a forward HL02 reference (a child declared before its
  // parent) would silently resolve, and a 2-node mutual-parent cycle would
  // drop both nodes into each other's children and vanish from `shipments`
  // entirely. A node's own id is added to seenIds only after its own
  // parent has been resolved, so the first of a cyclic pair never resolves
  // (its parent isn't seen yet) and surfaces as a root instead, while the
  // second resolves normally against it.
  const shipments: HLNode856[] = [];
  const seenIds = new Set<string>();
  for (const node of orderedNodes) {
    const parentId = node.parentId;
    const parent = parentId !== undefined ? nodesById.get(parentId) : undefined;
    if (
      parent &&
      parent !== node &&
      parentId !== undefined &&
      seenIds.has(parentId)
    ) {
      parent.children.push(node);
    } else {
      shipments.push(node);
    }
    if (node.id) seenIds.add(node.id);
  }

  return {header, shipments, summary};
}
